import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, differenceInWeeks, differenceInMonths, differenceInYears, differenceInDays, startOfDay, format } from 'date-fns';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { useToast } from '../components/ui/Toast';
import { useSubscription } from '../hooks/useSubscription';
import { FeatureLock } from '../components/FeatureLock';
import { TrendingUp, Camera, Calendar, Flame, Award, RefreshCw, Download } from 'lucide-react';
import { generateMonthlyReport, generatePDFContent, downloadReportAsPDF } from '../services/reportGenerator';
import { formatStreakLabel, calculateStreakFromScans } from '../utils/streakCalculator';
import { getScanDisplayName } from '../utils/scanUtils';
import {
  listUserScans,
  getSignedScanAssetUrl,
  type ScanRecord,
} from '../services/scanService';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: slideUp 0.5s ease-out;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;

  body.native-app & {
    margin-left: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled(Card)<{ delay?: string }>`
  text-align: center;
  animation: slideUp 0.6s ease-out;
  animation-delay: ${(props) => props.delay || '0s'};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius.xl};
  color: ${theme.colors.primary[600]};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatNumber = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.mint[500]} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const ChartCard = styled(Card)`
  margin-bottom: 2rem;
  animation: slideUp 0.7s ease-out;
`;

const ChartTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const LineChartContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 300px;
  padding: 1rem 0.5rem 2rem;
  margin-bottom: 1rem;
  overflow-x: auto;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 1rem 0.25rem 2rem;
  }
`;

const SVGChart = styled.svg`
  width: 100%;
  min-width: 600px;
  height: 300px;
  overflow: visible;
  
  @media (max-width: ${theme.breakpoints.md}) {
    min-width: 500px;
    height: 250px;
  }
`;

const ChartGrid = styled.g`
  stroke: ${theme.colors.border.light};
  stroke-width: 1;
  opacity: 0.3;
`;

const ChartLine = styled.path`
  fill: none;
  stroke: url(#lineGradient);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  animation: drawLine 1.5s ease-out;
  
  @keyframes drawLine {
    from {
      stroke-dasharray: 1000;
      stroke-dashoffset: 1000;
    }
    to {
      stroke-dasharray: 1000;
      stroke-dashoffset: 0;
    }
  }
`;

const ChartArea = styled.path`
  fill: url(#areaGradient);
  opacity: 0.2;
  animation: fadeIn 1s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.2;
    }
  }
`;

const ChartPoint = styled.circle`
  fill: ${theme.colors.primary[500]};
  stroke: white;
  stroke-width: 2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    r: 6;
    fill: ${theme.colors.primary[600]};
    filter: drop-shadow(0 0 8px ${theme.colors.primary[400]});
  }
`;

const PointLabel = styled.text`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  fill: ${theme.colors.text.primary};
  text-anchor: middle;
  pointer-events: none;
`;

const XAxis = styled.g`
  .tick {
    stroke: ${theme.colors.border.light};
    stroke-width: 1;
  }
  
  .tick-label {
    font-size: ${theme.typography.fontSize.xs};
    fill: ${theme.colors.text.tertiary};
    text-anchor: middle;
  }
`;

const YAxis = styled.g`
  .tick {
    stroke: ${theme.colors.border.light};
    stroke-width: 1;
  }
  
  .tick-label {
    font-size: ${theme.typography.fontSize.xs};
    fill: ${theme.colors.text.tertiary};
    text-anchor: end;
  }
`;

const AxisLabel = styled.text`
  font-size: ${theme.typography.fontSize.sm};
  fill: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const TimelineCard = styled(Card)`
  animation: slideUp 0.8s ease-out;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 1rem;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 31px;
    top: 64px;
    width: 2px;
    height: calc(100% - 32px);
    background: ${theme.colors.border.light};
  }
`;

const TimelineImage = styled.div<{ $src?: string }>`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$src ? `url(${props.$src})` : theme.colors.glow.mint};
  background-size: cover;
  background-position: center;
  border: 3px solid ${theme.colors.primary[400]};
  box-shadow: ${theme.shadows.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[500]};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineDate = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.tertiary};
  margin-bottom: 0.25rem;
`;

const TimelineTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const TimelineScore = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius['3xl']};
  color: ${theme.colors.primary[400]};

  svg {
    width: 40px;
    height: 40px;
  }
`;

const ProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const { getCurrentSession, userProfile } = useAuth();
  const { showToast } = useToast();
  const { checkFeature, tier } = useSubscription();
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Plus users get basic progress insights, Pro+ get weekly insights, Elite gets medical reports
  // All tiers have access, but with different feature levels

  // Fetch user scans
  useEffect(() => {
    const fetchScans = async () => {
      try {
        const session = await getCurrentSession();
        if (!session) {
          setError('Please sign in to view your progress.');
          setLoading(false);
          return;
        }

        const uid = session.getIdToken().payload.sub;
        const response = await listUserScans(session, uid, 50); // Get up to 50 scans for history
        
        // Filter only completed scans
        const completedScans = response.items.filter(scan => scan.status === 'complete');
        setScans(completedScans);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch scans for progress', err);
        setError(err.message || 'Failed to load progress data.');
      } finally {
        setLoading(false);
      }
    };

    fetchScans();
  }, [getCurrentSession]);

  // Create data points for line chart - show ALL scans as individual points
  const chartData = useMemo(() => {
    if (scans.length === 0) return { dataPoints: [], timeUnit: 'day' as 'day' | 'week' | 'month' | 'year', labels: [] };

    const now = new Date();
    const completedScans = scans
      .filter(s => s.status === 'complete')
      .sort((a, b) => new Date(a.createdAt ?? a.timestamp).getTime() - new Date(b.createdAt ?? b.timestamp).getTime());

    if (completedScans.length === 0) return { dataPoints: [], timeUnit: 'day' as 'day' | 'week' | 'month' | 'year', labels: [] };

    // Determine time unit based on oldest scan
    const oldestScan = completedScans[0];
    const oldestDate = new Date(oldestScan.createdAt ?? oldestScan.timestamp);
    const daysSinceOldest = differenceInDays(now, oldestDate);
    const yearsAgo = differenceInYears(now, oldestDate);
    const monthsAgo = differenceInMonths(now, oldestDate);

    let timeUnit: 'day' | 'week' | 'month' | 'year' = 'day';

    if (yearsAgo >= 1) {
      timeUnit = 'year';
    } else if (monthsAgo >= 1) {
      timeUnit = 'month';
    } else if (daysSinceOldest >= 7) {
      timeUnit = 'week';
    } else {
      timeUnit = 'day';
    }

    // Create data points from ALL scans (not grouped)
    const dataPoints: Array<{ label: string; score: number; timeIndex: number; date: Date }> = [];
    
    completedScans.forEach((scan, index) => {
      const scanDate = new Date(scan.createdAt ?? scan.timestamp);
      const score = scan.analysis?.skinHealthIndex ?? scan.glowScore ?? 0;
      
      if (score > 0) {
        let label: string;
        const daysAgo = differenceInDays(now, scanDate);
        
        if (timeUnit === 'year') {
          const yearsAgo = differenceInYears(now, scanDate);
          label = yearsAgo === 0 ? 'This Year' : yearsAgo === 1 ? 'Last Year' : `${yearsAgo} Years Ago`;
        } else if (timeUnit === 'month') {
          const monthsAgo = differenceInMonths(now, scanDate);
          label = monthsAgo === 0 ? 'This Month' : monthsAgo === 1 ? 'Last Month' : `${monthsAgo} Months Ago`;
        } else if (timeUnit === 'week') {
          const weeksAgo = differenceInWeeks(now, scanDate);
          label = weeksAgo === 0 ? 'This Week' : weeksAgo === 1 ? 'Last Week' : `${weeksAgo} Weeks Ago`;
        } else {
          // Day labels: "Day 1", "Day 2", "Today", "Yesterday", etc.
          if (daysAgo === 0) {
            label = 'Today';
          } else if (daysAgo === 1) {
            label = 'Yesterday';
          } else {
            label = `Day ${index + 1}`;
          }
        }
        
        dataPoints.push({
          label,
          score: Math.round(score),
          timeIndex: index,
          date: scanDate,
        });
      }
    });

    return { dataPoints, timeUnit, labels: dataPoints.map(d => d.label) };
  }, [scans]);

  // Calculate stats from real data
  const stats = useMemo(() => {
    const completedScans = scans.filter(s => s.status === 'complete');
    const totalScans = completedScans.length;
    
    // Current score (latest scan)
    const latestScan = completedScans.sort(
      (a, b) => new Date(b.createdAt ?? b.timestamp).getTime() - new Date(a.createdAt ?? a.timestamp).getTime()
    )[0];
    const currentScore = latestScan 
      ? (latestScan.analysis?.skinHealthIndex ?? latestScan.glowScore ?? 0)
      : 0;

    // First scan score
    const firstScan = completedScans.sort(
      (a, b) => new Date(a.createdAt ?? a.timestamp).getTime() - new Date(b.createdAt ?? b.timestamp).getTime()
    )[0];
    const firstScore = firstScan 
      ? (firstScan.analysis?.skinHealthIndex ?? firstScan.glowScore ?? 0)
      : 0;

    // Improvement
    const improvement = currentScore > 0 && firstScore > 0 ? currentScore - firstScore : 0;

    // Day streak - calculate from actual scan data (Snapchat-style)
    // This ensures accuracy by looking at all scans, not just stored value
    const streakData = calculateStreakFromScans(scans);
    const streakCount = streakData.streakCount;
    const streakLabel = formatStreakLabel(streakCount);

    return {
      streak: streakCount,
      streakLabel,
      totalScans,
      currentScore: Math.round(currentScore),
      improvement: improvement > 0 ? Math.round(improvement) : 0,
    };
  }, [scans, userProfile]);

  // Timeline data from real scans
  const timelineData = useMemo(() => {
    // First, get all completed scans sorted chronologically (oldest first) to determine scan numbers
    const allCompletedScans = scans
      .filter(s => s.status === 'complete')
      .sort(
        (a, b) => new Date(a.createdAt ?? a.timestamp).getTime() - new Date(b.createdAt ?? b.timestamp).getTime()
      );
    
    // Create a map of scanId to scan number (1-based, where 1 is the oldest)
    const scanNumberMap = new Map<string, number>();
    allCompletedScans.forEach((scan, index) => {
      scanNumberMap.set(scan.scanId, index + 1);
    });

    // Now get the latest 4 scans (newest first) for display
    const latestScans = allCompletedScans
      .slice()
      .reverse() // Reverse to get newest first
      .slice(0, 4);

    return latestScans.map((scan, index) => {
      const scanDate = new Date(scan.createdAt ?? scan.timestamp);
      const score = scan.analysis?.skinHealthIndex ?? scan.glowScore ?? 0;
      const dateStr = formatDistanceToNow(scanDate, { addSuffix: true });
      const scanNumber = scanNumberMap.get(scan.scanId) ?? 0;
      
      let emoji = '🌟';
      if (index === 0) emoji = '✨';
      else if (index === 1) emoji = '💚';
      else if (index === 2) emoji = '🌱';
      else emoji = '📸';

      return {
        date: dateStr,
        title: getScanDisplayName(scan, scanNumber), // Use custom name or "Scan N"
        score: Math.round(score),
        emoji,
        scanId: scan.scanId,
      };
    });
  }, [scans]);

  // Render line chart component
  const renderLineChart = () => {
    if (chartData.dataPoints.length === 0) return null;

    const padding = { top: 20, right: 40, bottom: 50, left: 60 };
    const chartWidth = 800;
    const chartHeight = 300;
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    const minScore = 0;
    const maxScore = 100;
    const scoreRange = maxScore - minScore;

    // Calculate X and Y scales
    const xScale = (index: number) => {
      if (chartData.dataPoints.length === 1) return padding.left + innerWidth / 2;
      return padding.left + (index / (chartData.dataPoints.length - 1)) * innerWidth;
    };

    const yScale = (score: number) => {
      const normalizedScore = (score - minScore) / scoreRange;
      return padding.top + innerHeight - (normalizedScore * innerHeight);
    };

    // Generate path for line
    const linePath = chartData.dataPoints
      .map((point, index) => {
        const x = xScale(index);
        const y = yScale(point.score);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');

    // Generate path for area under line
    const areaPath = chartData.dataPoints
      .map((point, index) => {
        const x = xScale(index);
        const y = yScale(point.score);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ') + 
      ` L ${xScale(chartData.dataPoints.length - 1)} ${padding.top + innerHeight}` +
      ` L ${xScale(0)} ${padding.top + innerHeight} Z`;

    // Y-axis ticks (0, 25, 50, 75, 100)
    const yTicks = [0, 25, 50, 75, 100];

    return (
      <LineChartContainer>
        <SVGChart viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={theme.colors.primary[500]} />
              <stop offset="100%" stopColor={theme.colors.mint[500]} />
            </linearGradient>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={theme.colors.primary[500]} />
              <stop offset="100%" stopColor={theme.colors.mint[500]} />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <ChartGrid>
            {yTicks.map(tick => {
              const y = yScale(tick);
              return (
                <line
                  key={`grid-${tick}`}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerWidth}
                  y2={y}
                />
              );
            })}
          </ChartGrid>

          {/* Area under line */}
          <ChartArea d={areaPath} />

          {/* Line */}
          <ChartLine d={linePath} />

          {/* Data points */}
          {chartData.dataPoints.map((point, index) => {
            const x = xScale(index);
            const y = yScale(point.score);
            return (
              <g key={`point-${index}`}>
                <ChartPoint
                  cx={x}
                  cy={y}
                  r={4}
                  data-score={point.score}
                  data-label={point.label}
                />
                <PointLabel
                  x={x}
                  y={y - 12}
                >
                  {point.score}
                </PointLabel>
              </g>
            );
          })}

          {/* Y-axis */}
          <YAxis>
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={padding.top + innerHeight}
              stroke={theme.colors.border.light}
              strokeWidth={2}
            />
            {yTicks.map(tick => {
              const y = yScale(tick);
              return (
                <g key={`y-tick-${tick}`}>
                  <line
                    x1={padding.left - 5}
                    y1={y}
                    x2={padding.left}
                    y2={y}
                    className="tick"
                  />
                  <text
                    x={padding.left - 10}
                    y={y + 4}
                    className="tick-label"
                  >
                    {tick}
                  </text>
                </g>
              );
            })}
            <AxisLabel
              x={-chartHeight / 2}
              y={15}
              transform={`rotate(-90, 15, ${chartHeight / 2})`}
            >
              Skin Health Index
            </AxisLabel>
          </YAxis>

          {/* X-axis */}
          <XAxis>
            <line
              x1={padding.left}
              y1={padding.top + innerHeight}
              x2={padding.left + innerWidth}
              y2={padding.top + innerHeight}
              stroke={theme.colors.border.light}
              strokeWidth={2}
            />
            {chartData.dataPoints.map((point, index) => {
              const x = xScale(index);
              return (
                <g key={`x-tick-${index}`}>
                  <line
                    x1={x}
                    y1={padding.top + innerHeight}
                    x2={x}
                    y2={padding.top + innerHeight + 5}
                    className="tick"
                  />
                  <text
                    x={x}
                    y={padding.top + innerHeight + 20}
                    className="tick-label"
                  >
                    {point.label.length > 10 ? point.label.substring(0, 8) + '...' : point.label}
                  </text>
                </g>
              );
            })}
            <AxisLabel
              x={padding.left + innerWidth / 2}
              y={chartHeight - 5}
            >
              Time ({chartData.timeUnit === 'year' ? 'Years' : chartData.timeUnit === 'month' ? 'Months' : chartData.timeUnit === 'week' ? 'Weeks' : 'Days'})
            </AxisLabel>
          </XAxis>
        </SVGChart>
      </LineChartContainer>
    );
  };

  return (
    <PageContainer>
      <Container>
        <Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <Title>Glow Progress Tracker 📊</Title>
              <Subtitle>
                Track your journey to radiant skin. You're glowing already! ✨
              </Subtitle>
            </div>
            {tier === 'elite' && scans.length > 0 && (
              <Button
                variant="primary"
                onClick={() => {
                  const report = generateMonthlyReport(scans);
                  const htmlContent = generatePDFContent(report, userProfile?.name || 'User');
                  const filename = `GlowLens-Report-${report.month}-${report.year}.html`;
                  downloadReportAsPDF(htmlContent, filename);
                  showToast('Monthly report generated! Open the downloaded file and use Print to PDF.', 'success');
                }}
                style={{ alignSelf: 'flex-start' }}
              >
                <Download size={18} style={{ marginRight: '0.5rem' }} />
                Export Monthly Report
              </Button>
            )}
          </div>
        </Header>

        <StatsGrid>
          <StatCard padding="md" delay="0.1s">
            <StatIcon>
              <Flame />
            </StatIcon>
            <StatNumber>{stats.streakLabel || stats.streak}</StatNumber>
            <StatLabel>Streak 🔥</StatLabel>
          </StatCard>

          <StatCard padding="md" delay="0.2s">
            <StatIcon>
              <Camera />
            </StatIcon>
            <StatNumber>{stats.totalScans}</StatNumber>
            <StatLabel>Total Scans</StatLabel>
          </StatCard>

          <StatCard padding="md" delay="0.3s">
            <StatIcon>
              <TrendingUp />
            </StatIcon>
            <StatNumber>{stats.currentScore || '—'}</StatNumber>
            <StatLabel>Current Score</StatLabel>
          </StatCard>

          <StatCard padding="md" delay="0.4s">
            <StatIcon>
              <Award />
            </StatIcon>
            <StatNumber>{stats.improvement > 0 ? `+${stats.improvement}` : stats.improvement}</StatNumber>
            <StatLabel>Improvement</StatLabel>
          </StatCard>
        </StatsGrid>

        <ChartCard padding="lg">
          <ChartTitle>
            <TrendingUp />
            GlowScore™ History
          </ChartTitle>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem', color: theme.colors.primary[500] }} />
              <p style={{ color: theme.colors.text.secondary }}>Loading your progress...</p>
            </div>
          ) : chartData.dataPoints.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Camera />
              </EmptyIcon>
              <h3 style={{ marginBottom: '0.5rem', color: theme.colors.text.primary }}>
                No scan history yet
              </h3>
              <p style={{ marginBottom: '1.5rem' }}>
                Take your first scan to start tracking your progress!
              </p>
              <Button onClick={() => navigate('/scan')}>
                <Camera size={20} />
                Take First Scan
              </Button>
            </EmptyState>
          ) : (
            renderLineChart()
          )}
        </ChartCard>

        <TimelineCard padding="lg">
          <ChartTitle>
            <Calendar />
            Scan Timeline
          </ChartTitle>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem', color: theme.colors.primary[500] }} />
              <p style={{ color: theme.colors.text.secondary }}>Loading timeline...</p>
            </div>
          ) : timelineData.length > 0 ? (
            <Timeline>
              {timelineData.map((item, index) => (
                <TimelineItem 
                  key={item.scanId || index}
                  style={{ cursor: item.scanId ? 'pointer' : 'default' }}
                  onClick={() => item.scanId && navigate(`/results/${item.scanId}`)}
                >
                  <TimelineImage>
                    <span style={{ fontSize: '2rem' }}>{item.emoji}</span>
                  </TimelineImage>
                  <TimelineContent>
                    <TimelineDate>{item.date}</TimelineDate>
                    <TimelineTitle>{item.title}</TimelineTitle>
                    <TimelineScore>
                      GlowScore: <strong>{item.score}</strong>
                      {index > 0 && item.score > timelineData[index - 1].score && (
                        <Badge variant="success" size="sm" style={{ marginLeft: '0.5rem' }}>
                          +{item.score - timelineData[index - 1].score} improvement
                        </Badge>
                      )}
                    </TimelineScore>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <Camera />
              </EmptyIcon>
              <h3 style={{ marginBottom: '0.5rem', color: theme.colors.text.primary }}>
                No scans yet
              </h3>
              <p style={{ marginBottom: '1.5rem' }}>
                Start your glow journey by taking your first skin scan!
              </p>
              <Button onClick={() => window.location.href = '/scan'}>
                <Camera size={20} />
                Take First Scan
              </Button>
            </EmptyState>
          )}
        </TimelineCard>

        <div style={{ marginTop: '2rem', textAlign: 'center' } as React.CSSProperties}>
          <p style={{ color: theme.colors.text.secondary, marginBottom: '1rem' }}>
            Keep going! Consistency is key to seeing real progress 🌱
          </p>
          <Button size="lg" onClick={() => navigate('/scan')}>
            <Camera size={20} />
            Take New Scan
          </Button>
        </div>
      </Container>
    </PageContainer>
  );
};

export default ProgressPage;
